import React, { useEffect, useRef } from 'react'
import { useTheme, THEMES } from '../../utils/ThemeContext'

const StarryCanvas = () => {
  const canvasRef = useRef(null)
  const { theme } = useTheme()
  const mouseRef = useRef({ x: null, y: null, radius: 140 })
  const isVisibleRef = useRef(true)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext ? canvas.getContext('2d') : null
    if (!ctx) return
    let animationFrameId
    let particles = []

    // Check prefers-reduced-motion
    const prefersReducedMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      initParticles()
    }

    // Color palette based on theme
    const getColors = () => {
      switch (theme) {
        case THEMES.SUNSET:
          return {
            stars: ['#f43f5e', '#fb7185', '#f59e0b', '#fde68a', '#ffffff'],
            lineRgba: 'rgba(244, 63, 94, '
          }
        case THEMES.EMERALD:
          return {
            stars: ['#10b981', '#34d399', '#06b6d4', '#6ee7b7', '#ffffff'],
            lineRgba: 'rgba(16, 185, 129, '
          }
        case THEMES.LIGHT:
          return {
            stars: ['#6366f1', '#3b82f6', '#0284c7', '#818cf8'],
            lineRgba: 'rgba(99, 102, 241, '
          }
        case THEMES.AURORA:
        default:
          return {
            stars: ['#818cf8', '#6366f1', '#38bdf8', '#06b6d4', '#c084fc', '#ffffff'],
            lineRgba: 'rgba(99, 102, 241, '
          }
      }
    }

    const initParticles = () => {
      particles = []
      // Scale count with screen width (40 on mobile, up to 90 on desktop)
      const count = Math.floor(Math.min(window.innerWidth / 16, 85))
      const { stars } = getColors()

      for (let i = 0; i < count; i++) {
        const size = Math.random() * 2.2 + 0.6
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size,
          baseSize: size,
          speedX: (Math.random() - 0.5) * (prefersReducedMotion ? 0 : 0.4),
          speedY: (Math.random() - 0.5) * (prefersReducedMotion ? 0 : 0.4),
          color: stars[Math.floor(Math.random() * stars.length)],
          twinkleSpeed: Math.random() * 0.03 + 0.01,
          twinkleAngle: Math.random() * Math.PI * 2,
          opacity: Math.random() * 0.6 + 0.3
        })
      }
    }

    const draw = () => {
      if (!isVisibleRef.current) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const { lineRgba } = getColors()

      // Draw constellation lines between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < 110) {
            const alpha = (1 - dist / 110) * 0.22
            ctx.beginPath()
            ctx.strokeStyle = `${lineRgba}${alpha})`
            ctx.lineWidth = 0.75
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.stroke()
          }
        }
      }

      // Draw and update each star
      particles.forEach(p => {
        // Star twinkling
        p.twinkleAngle += p.twinkleSpeed
        const currentOpacity = p.opacity + Math.sin(p.twinkleAngle) * 0.25

        // Mouse interaction (gentle attraction / hover push)
        if (mouseRef.current.x !== null) {
          const dx = mouseRef.current.x - p.x
          const dy = mouseRef.current.y - p.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < mouseRef.current.radius) {
            const force = (1 - dist / mouseRef.current.radius) * 1.5
            p.x -= (dx / dist) * force
            p.y -= (dy / dist) * force
          }
        }

        // Particle position drift
        p.x += p.speedX
        p.y += p.speedY

        // Wrap edges
        if (p.x < 0) p.x = canvas.width
        if (p.x > canvas.width) p.x = 0
        if (p.y < 0) p.y = canvas.height
        if (p.y > canvas.height) p.y = 0

        // Draw star
        ctx.beginPath()
        ctx.arc(p.x, p.y, Math.max(0.5, p.size), 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.globalAlpha = Math.min(Math.max(currentOpacity, 0.1), 0.95)
        ctx.shadowColor = p.color
        ctx.shadowBlur = p.size > 1.8 ? 8 : 2
        ctx.fill()
        ctx.shadowBlur = 0
        ctx.globalAlpha = 1
      })

      animationFrameId = requestAnimationFrame(draw)
    }

    const handleMouseMove = e => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
    }

    const handleMouseLeave = () => {
      mouseRef.current.x = null
      mouseRef.current.y = null
    }

    // Intersection observer to pause loop when Hero scrolls away
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting
        if (entry.isIntersecting) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = requestAnimationFrame(draw)
        }
      },
      { threshold: 0.05 }
    )

    observer.observe(canvas)
    resizeCanvas()
    draw()

    window.addEventListener('resize', resizeCanvas)
    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    window.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener('resize', resizeCanvas)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseleave', handleMouseLeave)
      observer.disconnect()
    }
  }, [theme])

  return (
    <canvas
      ref={canvasRef}
      className="hero-starry-canvas"
      aria-hidden="true"
    />
  )
}

export default StarryCanvas
